import React from "react";
import useDebugInformation from "../../../hooks/useDebugInformation";
import { ui } from "../../../interfaces/interfaces";
import Grid, { GridSize } from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import { Paper } from "@mui/material";
import { StringProcessor } from "@utils/stringProcessor";
import { cacheUtils } from "../../..";
import { useAppSelector } from "@store/hooks";
import { CacheTypes } from "@utils/cache/interfaces";
import useRecalculatePayments from "../../../hooks/useRecalculatePayments";

const name = "BillingMethodTab"

type MemoizedInputProps = {
    parent: string
    prefix: Props["prefix"]
    inputName: "PaymentType"
    xs: GridSize
    sm: GridSize
}

const dropdownChange = (v: any, prefix: Props["prefix"], inputName: "PaymentType") => {
    ui[`${prefix}Method${inputName}`] = v
}

const MemoizedDropdownInput = React.memo<MemoizedInputProps>(({ parent, prefix, inputName, xs, sm }) => {
    const duration = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsDuration)
    const freq = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsBillingFrequency)
    const value = useAppSelector(({ ui }) => ui[`${prefix}Method${inputName}`])
    const options = cacheUtils.getCache<CacheTypes.PaymentTypes>("PaymentTypes");
    const [disableCards, setDisableCards] = React.useState(false)

    React.useEffect(() => {
        setDisableCards(duration?.serviceAgreementDurationId === 18 && (freq?.serviceAgreementBillingFrequencyId === 12 || freq?.serviceAgreementBillingFrequencyId === 18))
    }, [duration, freq])

    disableCards && dropdownChange(options.find(o => o?.name === "Direct Debit"), prefix, inputName)

    const helperText = useAppSelector(({ ui }) => ui[`${prefix}Method${inputName}HelperText`])

    const valid = useAppSelector(({ state }) => state[`${prefix}Method${inputName}Valid`])

    useDebugInformation(`${parent}▷[${inputName}]`, { inputName, parent, prefix, xs, sm, options })
    return (
        (<Grid
            size={{
                xs,
                sm
            }}>
            <Autocomplete
                id={`combo-box-${inputName}`}
                options={options}
                getOptionLabel={option => option?.name ? option.name : ""}
                getOptionDisabled={option => disableCards && option?.name !== "Direct Debit"}
                onChange={(e, v) => dropdownChange(v, prefix, inputName)}
                sx={{ mt: 1 }}
                value={value as CacheTypes.PaymentFrequencies | CacheTypes.PaymentTypes | null | undefined || null}
                renderInput={(params: any) => <TextField required {...params} label={StringProcessor.camelToWords(inputName).replace("Types", "Method").replace("Frequencies", "Frequency")} name={StringProcessor.camelToWords(inputName)} error={!valid} color={valid ? "primary" : "error"} helperText={!valid ? helperText : ""} />}
            />
        </Grid>)
    );
})

type Props = {
    prefix: "CarePlanBilling";
    parent: string
}

export const BillingMethodTabComponent: React.FC<Props> = ({ prefix, parent }) => {
    const debugName = `${parent}▷[${name}]`
    const isBillingMethodValid = useAppSelector(({ state }) => state.CarePlanBillingMethodPaymentTypeValid)
    // monitor for changes resulting from effect action
    const carePlanSummary = useAppSelector(({ ui }) => ui.CarePlanSummary)
    useRecalculatePayments()

    useDebugInformation(debugName, { prefix, parent, carePlanSummary })
    return (
        <>
            <Grid container spacing={2} style={{ overflow: "hidden" }}>
                <MemoizedDropdownInput inputName="PaymentType" parent={debugName} prefix={prefix} xs={12} sm={12} />
            </Grid>
            {isBillingMethodValid && <Paper elevation={4} sx={{ p: 1, mt: 1 }}><Typography>{carePlanSummary}</Typography></Paper>}
        </>
    )
};

export default React.memo(BillingMethodTabComponent)